import * as React from 'react';
import {
  ArrowCounterClockwise,
  CircleNotch,
  GithubLogo,
  Wrench,
  X,
} from '@phosphor-icons/react';
import type { RowSelectionState } from '@tanstack/react-table';
import { formatCategoryLabel } from '../findings-display-model';
import type { StatusPatchSummary } from '../findings-operator-model';
import type { FindingDoc } from '../findings-types';
import s from '../findings.module.scss';

export type FixableCategoryBatch = {
  category: string;
  count: number;
  fileCount: number;
  ids: string[];
};

type RepositoryBulkActionBarProps = {
  selectedIds: string[];
  selectedSummaryParts: Array<string | undefined>;
  selectedOpenIds: string[];
  selectedFixableIds: string[];
  selectedResolvedOrIgnoredIds: string[];
  selectedFindings: FindingDoc[];
  selectedFixPlanMode: 'direct' | 'review' | 'batch';
  selectedFixableCategoryBatches: FixableCategoryBatch[];
  selectedCategoryBatch: FixableCategoryBatch | null;
  fixPlanVisible: boolean;
  fixPlanCategory: string | null;
  fixLoading: boolean;
  issueLoading: boolean;
  fixPrCreated: boolean;
  canCreateWaiver: boolean;
  canCreateAutofix: boolean;
  canCreateGitHubIssue: boolean;
  repoFullName?: string;
  reviewAutofixLimit: number;
  reviewAutofixFileLimit: number;
  plannedFixIds: string[];
  plannedFixCount: number;
  plannedFixFileCount: number;
  canCreatePlannedFix: boolean;
  setFixPlanOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setFixPlanCategory: React.Dispatch<React.SetStateAction<string | null>>;
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  onBulkStatus: (
    ids: string[],
    status: 'open' | 'ignored' | 'resolved',
  ) => Promise<StatusPatchSummary | null>;
  onAutofix: (ids: string[]) => Promise<void>;
  onCreateIssue: (findings: FindingDoc[]) => void;
};

export function RepositoryBulkActionBar({
  selectedIds,
  selectedSummaryParts,
  selectedOpenIds,
  selectedFixableIds,
  selectedResolvedOrIgnoredIds,
  selectedFindings,
  selectedFixPlanMode,
  selectedFixableCategoryBatches,
  selectedCategoryBatch,
  fixPlanVisible,
  fixPlanCategory,
  fixLoading,
  issueLoading,
  fixPrCreated,
  canCreateWaiver,
  canCreateAutofix,
  canCreateGitHubIssue,
  repoFullName,
  reviewAutofixLimit,
  reviewAutofixFileLimit,
  plannedFixIds,
  plannedFixCount,
  plannedFixFileCount,
  canCreatePlannedFix,
  setFixPlanOpen,
  setFixPlanCategory,
  setRowSelection,
  onBulkStatus,
  onAutofix,
  onCreateIssue,
}: RepositoryBulkActionBarProps) {
  if (selectedIds.length === 0) return null;

  return (
    <div
      className={s.bulkActionBar}
      data-plan-open={fixPlanVisible || undefined}
    >
      <span className={s.bulkActionCount}>
        {selectedIds.length} selected
        {selectedSummaryParts.length > 0
          ? ` · ${selectedSummaryParts.join(' · ')}`
          : ''}
      </span>
      <span className={s.bulkActionScope}>
        Actions apply only to selected rows. Ignore affects current findings;
        Reopen affects historical findings.
      </span>
      {!fixPlanVisible && (
        <div className={s.bulkActionButtons}>
          {selectedOpenIds.length > 0 && (
            <button
              type="button"
              className={s.bulkActionBtn}
              disabled={!canCreateWaiver}
              title={
                canCreateWaiver
                  ? undefined
                  : 'Upgrade to Pro to ignore findings with cloud waivers.'
              }
              onClick={async () => {
                const result = await onBulkStatus(selectedOpenIds, 'ignored');
                if (result) setRowSelection({});
              }}
            >
              Ignore current {selectedOpenIds.length}
            </button>
          )}
          {selectedFixableIds.length > 0 && !fixPrCreated && (
            <button
              type="button"
              className={
                selectedFixPlanMode === 'direct'
                  ? s.bulkActionBtnAccent
                  : s.bulkActionBtn
              }
              disabled={fixLoading || !canCreateAutofix}
              title={
                canCreateAutofix
                  ? undefined
                  : 'Upgrade to a paid plan with GitHub automation to create fix PRs.'
              }
              onClick={async () => {
                if (!canCreateAutofix) return;
                if (selectedFixPlanMode === 'direct') {
                  await onAutofix(selectedFixableIds);
                  setRowSelection({});
                } else {
                  setFixPlanOpen((open) => !open);
                }
              }}
            >
              {selectedFixPlanMode === 'direct' ? (
                <>
                  <Wrench size={11} />
                  {canCreateAutofix
                    ? `Create fix PR for ${selectedFixableIds.length}`
                    : 'Upgrade for fix PRs'}
                </>
              ) : (
                <>
                  {canCreateAutofix
                    ? 'Review fix PR scope'
                    : 'Upgrade for fix PRs'}
                </>
              )}
            </button>
          )}
          {selectedResolvedOrIgnoredIds.length > 0 && (
            <button
              type="button"
              className={s.bulkActionBtn}
              onClick={async () => {
                const result = await onBulkStatus(
                  selectedResolvedOrIgnoredIds,
                  'open',
                );
                if (result) setRowSelection({});
              }}
            >
              <ArrowCounterClockwise size={11} />
              Reopen historical {selectedResolvedOrIgnoredIds.length}
            </button>
          )}
          {repoFullName && canCreateGitHubIssue && (
            <button
              type="button"
              className={s.bulkActionBtnAccent}
              disabled={issueLoading}
              onClick={() => onCreateIssue(selectedFindings)}
            >
              {issueLoading ? (
                <CircleNotch size={11} className={s.spinIcon} />
              ) : (
                <GithubLogo size={12} />
              )}
              Create issue
            </button>
          )}
        </div>
      )}
      {fixPlanVisible && (
        <div className={s.fixPlanPanel} data-mode={selectedFixPlanMode}>
          <div className={s.fixPlanHeader}>
            <span>
              {selectedFixPlanMode === 'batch' ? 'Large fix set' : 'Fix plan'}
            </span>
            <button
              type="button"
              className={s.fixPlanClose}
              onClick={() => setFixPlanOpen(false)}
              aria-label="Close fix plan"
            >
              <X size={12} />
            </button>
          </div>
          <div className={s.fixPlanBody}>
            {!canCreateAutofix
              ? 'Autofix PRs require a paid plan with GitHub automation.'
              : selectedFixPlanMode === 'batch'
                ? `Choose one rule category for a smaller fix PR. Batches over ${reviewAutofixLimit} fixes or ${reviewAutofixFileLimit} files stay disabled until the selection is narrower.`
                : 'Choose one rule category to keep the generated fix PR focused and reviewable.'}
          </div>
          <div className={s.fixPlanBatches} aria-label="Fix batches">
            {selectedFixableCategoryBatches.map((batch) => {
              const active = fixPlanCategory === batch.category;
              const tooLarge =
                batch.count > reviewAutofixLimit ||
                batch.fileCount > reviewAutofixFileLimit;
              return (
                <button
                  key={batch.category}
                  type="button"
                  className={s.fixPlanBatch}
                  data-active={active || undefined}
                  data-too-large={tooLarge || undefined}
                  onClick={() =>
                    setFixPlanCategory((current) =>
                      current === batch.category ? null : batch.category,
                    )
                  }
                >
                  <span>{formatCategoryLabel(batch.category)}</span>
                  <strong>{batch.count}</strong>
                </button>
              );
            })}
          </div>
          {selectedCategoryBatch && (
            <div className={s.fixPlanSelection}>
              {canCreatePlannedFix
                ? `${formatCategoryLabel(selectedCategoryBatch.category)} will create ${plannedFixCount} fixes across ${plannedFixFileCount} files.`
                : `${formatCategoryLabel(selectedCategoryBatch.category)} has ${plannedFixCount} fixes across ${plannedFixFileCount} files. Select fewer rows before creating this PR.`}
            </div>
          )}
          <button
            type="button"
            className={s.fixPlanPrimary}
            disabled={fixLoading || !canCreatePlannedFix}
            title={canCreateAutofix ? undefined : 'Upgrade to create fix PRs.'}
            onClick={async () => {
              if (!canCreatePlannedFix) return;
              await onAutofix(plannedFixIds);
              setFixPlanOpen(false);
              setFixPlanCategory(null);
              setRowSelection({});
            }}
          >
            <Wrench size={11} />
            {selectedCategoryBatch
              ? `Create ${formatCategoryLabel(selectedCategoryBatch.category)} PR`
              : 'Select a category'}
          </button>
        </div>
      )}
      <button
        type="button"
        className={s.bulkActionDismiss}
        onClick={() => setRowSelection({})}
        aria-label="Clear selection"
      >
        <X size={12} />
      </button>
    </div>
  );
}
